import React, { useEffect, useState } from "react";
import { Mail, Pencil, Phone, Save, Trash2, X } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const emptyContact = { name: "", school: "", email: "", phone: "" };
const phoneDigits = (phone) => String(phone || "").replace(/\D/g, "");

export default function ContactBook() {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyContact);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setContacts((await api.get("/contacts")).data);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm(emptyContact);
    setEditingId(null);
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/contacts/${editingId}`, form);
        toast.success("تم تحديث جهة التواصل");
      } else {
        await api.post("/contacts", form);
        toast.success("تمت إضافة جهة التواصل");
      }
      reset();
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const remove = async (contact) => {
    if (!window.confirm(`حذف جهة التواصل ${contact.name}؟`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      toast.success("تم حذف جهة التواصل");
      if (editingId === contact.id) reset();
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>جهات التواصل</h1>
        <p className="mt-2 text-sm text-[#4A554E]">إدارة بيانات البريد الإلكتروني وأرقام واتساب للمدارس والمشرفين.</p>
      </div>

      <form onSubmit={save} className="grid gap-4 rounded-2xl border border-[#e6e2d8] bg-white p-5 md:grid-cols-2">
        <div>
          <Label className="mb-1.5 block">الاسم</Label>
          <Input value={form.name} required onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div>
          <Label className="mb-1.5 block">المدرسة أو الجهة</Label>
          <Input value={form.school} onChange={(event) => setForm({ ...form, school: event.target.value })} />
        </div>
        <div>
          <Label className="mb-1.5 block">البريد الإلكتروني</Label>
          <Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        </div>
        <div>
          <Label className="mb-1.5 block">رقم واتساب مع مفتاح الدولة</Label>
          <Input type="tel" value={form.phone} placeholder="9665xxxxxxxx" onChange={(event) => setForm({ ...form, phone: event.target.value })} />
        </div>
        <div className="flex flex-wrap gap-2 md:col-span-2">
          <Button type="submit" className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]">
            <Save className="me-2 h-4 w-4" />{editingId ? "حفظ التعديلات" : "إضافة جهة"}
          </Button>
          {editingId && <Button type="button" variant="outline" onClick={reset} className="rounded-xl"><X className="me-2 h-4 w-4" />إلغاء</Button>}
        </div>
      </form>

      <div className="grid gap-3 md:grid-cols-2">
        {loading ? <p className="text-sm text-[#4A554E]">جاري تحميل جهات التواصل...</p> : contacts.map((contact) => {
          const phone = phoneDigits(contact.phone);
          return (
            <article key={contact.id} className="rounded-2xl border border-[#e6e2d8] bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-[#1A201C]">{contact.name}</h2>
                  <p className="mt-1 text-sm text-[#4A554E]">{contact.school || "—"}</p>
                </div>
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" title="تعديل" onClick={() => { setEditingId(contact.id); setForm({ name: contact.name, school: contact.school || "", email: contact.email || "", phone: contact.phone || "" }); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" title="حذف" onClick={() => remove(contact)} className="text-[#B84B4B]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {contact.email && <a className="inline-flex items-center gap-2 rounded-lg border border-[#e6e2d8] px-3 py-2 text-sm text-[#3E6A56]" href={`mailto:${contact.email}`}><Mail className="h-4 w-4" />{contact.email}</a>}
                {phone && <a className="inline-flex items-center gap-2 rounded-lg border border-[#e6e2d8] px-3 py-2 text-sm text-[#3E6A56]" href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer"><Phone className="h-4 w-4" />واتساب</a>}
              </div>
            </article>
          );
        })}
        {!loading && contacts.length === 0 && <p className="text-sm text-[#4A554E]">لا توجد جهات تواصل محفوظة.</p>}
      </div>
    </div>
  );
}
